import { Button } from "@/components/ui/button";
import { GoogleLogo, PlugsConnected, Plugs, CheckCircle, Sparkle, ShieldCheck } from "@phosphor-icons/react";

export function ConfigView({ cfg, gstatus, onConnect, onDisconnect }) {
  if (!cfg) return null;
  const connected = gstatus?.connected;
  const rows = [
    ["Buzón monitorizado", cfg.mailbox],
    ["Frecuencia polling", `${cfg.poll_min} minutos`],
    ["Modelo IA Gemini", cfg.modelo || "gemini-flash-latest"],
    ["Modo de datos", cfg.mode === "real" ? "Google Workspace (Producción Real)" : "Mock (Facturas de prueba)"],
    ["Sheet de control", cfg.sheet_name || "Control de Facturas — cocoand"],
    ["Sheet ID", cfg.sheet_id || "—"],
    ["Pestañas en Sheet", "Facturas (OK / Abonos)  ·  Incidencias (Dudas / Descuadres)"],
    ["Carpeta raíz Drive", cfg.drive_root_name || "Facturas"],
    ["Drive root ID", cfg.drive_root_id || "—"],
  ];

  return (
    <div className="animate-fadein space-y-6" data-testid="config-view">
      {/* Tarjeta de Google Workspace */}
      <div className="bg-white rounded-md border border-slate-200 p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-slate-900 flex items-center justify-center shrink-0">
              <GoogleLogo size={20} weight="bold" className="text-white" />
            </div>
            <div>
              <div className="font-display text-lg font-bold tracking-tight text-slate-900">
                Conexión Google Workspace (Gmail + Drive + Sheets)
              </div>
              {connected ? (
                <div className="mt-1 flex items-center gap-1.5 text-sm text-emerald-600 font-medium" data-testid="google-connected">
                  <CheckCircle size={16} weight="fill" /> Conectado en producción como: {gstatus.email || "cuenta Workspace"}
                </div>
              ) : gstatus?.configured ? (
                <div className="mt-1 text-sm text-slate-600">
                  Credenciales OAuth de Google Cloud configuradas correctamente. Pulsa «Conectar Google» para autorizar el buzón.
                </div>
              ) : (
                <div className="mt-1 text-sm text-amber-600" data-testid="google-not-configured">
                  Faltan credenciales OAuth en el archivo .env del servidor.
                </div>
              )}
            </div>
          </div>
          {connected ? (
            <Button variant="outline" onClick={onDisconnect} data-testid="btn-google-disconnect" className="gap-2 text-slate-600 shrink-0">
              <Plugs size={16} /> Desconectar
            </Button>
          ) : (
            <Button
              onClick={onConnect}
              disabled={!gstatus?.configured}
              data-testid="btn-google-connect"
              className="gap-2 bg-slate-900 text-white hover:bg-slate-800 shrink-0"
            >
              <PlugsConnected size={16} /> Conectar Google
            </Button>
          )}
        </div>

        {/* Guía de configuración y estado */}
        <div className="mt-5 rounded-md bg-slate-50 border border-slate-200 p-4 text-xs text-slate-600 space-y-2">
          <div className="flex items-center gap-1.5 font-semibold text-slate-800">
            <ShieldCheck size={16} className="text-emerald-600" />
            Recomendación para Producción Permanente (Google Workspace)
          </div>
          <div>
            1. En <strong>Google Cloud Console</strong> → <strong>Pantalla de consentimiento de OAuth</strong>, asegúrate de seleccionarla como <strong>"Interna" (Internal)</strong> dentro del dominio <code className="font-mono bg-white px-1.5 py-0.5 rounded border border-slate-200">cocoand.com</code>. De este modo, los tokens de acceso no caducarán a los 7 días.
          </div>
          <div>
            2. <strong>URI de redirección autorizada</strong> (debe coincidir con la registrada en Google Cloud):
            <div className="mt-1 font-mono text-[11px] text-slate-800 break-all bg-white border border-slate-200 rounded px-2.5 py-1.5 select-all">
              {gstatus?.redirect_uri || "http://localhost:8000/api/google/oauth/callback"}
            </div>
          </div>
        </div>
      </div>

      {/* Tarjeta de Inteligencia Artificial Gemini */}
      <div className="bg-white rounded-md border border-slate-200 p-6">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center shrink-0">
            <Sparkle size={20} weight="fill" className="text-white" />
          </div>
          <div>
            <div className="font-display text-lg font-bold tracking-tight text-slate-900">
              Motor de Extracción IA (Google Gemini Flash)
            </div>
            <div className="mt-1 text-sm text-slate-600">
              Extracción especializada para facturas de hostelería española con validación aritmética de IVA (4%, 10% y 21%).
            </div>
            <div className="mt-2 text-xs text-slate-500">
              API Key configurada en el servidor. Volumen de ~100 facturas/mes con coste marginal estimado &lt; 0,30 €/mes.
            </div>
          </div>
        </div>
      </div>

      {/* Parámetros del sistema */}
      <div className="bg-white rounded-md border border-slate-200 divide-y divide-slate-100">
        <div className="px-5 py-3 text-xs font-bold uppercase tracking-[0.14em] text-slate-700 bg-slate-50/70">
          Parámetros del Sistema
        </div>
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between px-5 py-3.5 gap-4">
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 shrink-0">{k}</span>
            <span className="text-sm font-mono text-slate-700 text-right break-all">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
